import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";

const HIGHLIGHTS = [
    { title: "Professional Design", desc: "A look that instantly says you take your vision seriously." },
    { title: "Mobile Friendly", desc: "Beautiful and fast on the devices people actually use." },
    { title: "Built for Growth", desc: "A foundation that scales as your brand grows." },
    { title: "Easy Navigation", desc: "Visitors find what they need in seconds, not minutes." },
    { title: "Strong Calls-to-Action", desc: "Every page guides people toward reaching out." },
    { title: "Ongoing Support", desc: "You are never left alone after launch day." },
    { title: "Designed Around Your Vision", desc: "Your goals lead. The design follows." },
];

const Counter = ({ to, suffix, label, testid }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!inView) return undefined;
        const controls = animate(0, to, {
            duration: 1.8,
            ease: [0.22, 1, 0.36, 1],
            onUpdate: (v) => setVal(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, to]);
    return (
        <div ref={ref} data-testid={testid}>
            <p className="font-display text-3xl font-extrabold text-[#0A0F1D] sm:text-4xl">
                {val}
                <span className="text-electric-grad">{suffix}</span>
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">{label}</p>
        </div>
    );
};

export const WhyUs = () => (
    <section id="why" className="relative py-24 md:py-36" data-testid="why-section">
        <div className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-[#0066FF]/40 to-transparent" aria-hidden="true" />
        <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:gap-20 lg:px-8">
            <div className="lg:sticky lg:top-32 lg:self-start">
                <Reveal>
                    <Eyebrow>Why Footman</Eyebrow>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0A0F1D] sm:text-5xl">
                        YOUR WEBSITE SHOULD WORK AS <span className="text-electric-grad">HARD AS YOU DO.</span>
                    </h2>
                </Reveal>
                <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-6">
                    <Counter to={100} suffix="%" label="Custom Design" testid="stat-custom" />
                    <Counter to={14} suffix="+" label="Services Offered" testid="stat-services" />
                    <Counter to={5} suffix="" label="Step Process" testid="stat-process" />
                </div>
            </div>

            <div>
                {HIGHLIGHTS.map((h, i) => (
                    <motion.div
                        key={h.title}
                        data-testid={`why-highlight-${i}`}
                        className="group flex items-start gap-5 border-b border-[#0A0F1D]/10 py-6 transition-[padding] duration-500 hover:pl-3 first:border-t"
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.65, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0066FF]/15 text-[#0066FF] transition-[background-color,transform] duration-300 group-hover:scale-110 group-hover:bg-[#0066FF]/30">
                            <Check className="h-4 w-4" />
                        </span>
                        <div>
                            <h3 className="font-display text-lg font-bold text-[#0A0F1D] sm:text-xl">{h.title}</h3>
                            <p className="mt-1 text-sm leading-relaxed text-slate-600">{h.desc}</p>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);
